import { useEffect, useState, useCallback } from 'react'
import axios from 'axios'
import { Clock, Delete, CheckCircle, LogIn, Coffee, Play, LogOut, AlertCircle, WifiOff, XCircle, CloudUpload } from 'lucide-react'

const API = '/api'
const PIN_CACHE_KEY   = 'badge_pin_cache'
const QUEUE_KEY       = 'badge_offline_queue'
const PIN_CACHE_TTL   = 24 * 60 * 60 * 1000   // 24h

type EventType = 'arrive' | 'break' | 'resume' | 'leave'
type Screen    = 'pin' | 'action' | 'confirm' | 'offline' | 'error'

interface Employee { id: string; firstName: string; lastName: string }
interface Shift    { startTime: string; endTime: string; breakMinutes: number; position?: { name: string; color: string } | null }

// ── Cache PIN → employé (persistence 24h) ─────────────────────────
interface PinCacheEntry { employee: Employee; lastEvent: string | null; cachedAt: number }
function loadPinCache(): Record<string, PinCacheEntry> {
  try { return JSON.parse(localStorage.getItem(PIN_CACHE_KEY) || '{}') }
  catch { return {} }
}
function savePinCache(cache: Record<string, PinCacheEntry>) {
  // purger les entrées expirées
  const now = Date.now()
  const clean: Record<string, PinCacheEntry> = {}
  for (const [k, v] of Object.entries(cache)) {
    if (now - v.cachedAt < PIN_CACHE_TTL) clean[k] = v
  }
  localStorage.setItem(PIN_CACHE_KEY, JSON.stringify(clean))
}

// ── Queue offline ──────────────────────────────────────────────────
interface QueuedEvent {
  id: string
  employeeId: string
  employeeName: string
  establishmentId: string
  eventType: EventType
  queuedAt: string     // ISO — horodatage réel du pointage
}
function loadQueue(): QueuedEvent[] {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]') }
  catch { return [] }
}
function saveQueue(q: QueuedEvent[]) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q))
}
function genId() { return Math.random().toString(36).slice(2) + Date.now().toString(36) }

// ── Constants ──────────────────────────────────────────────────────
const EVENT_LABELS: Record<EventType, string> = {
  arrive: 'Arrivée', break: 'Début de pause', resume: 'Reprise', leave: 'Départ'
}
const NEXT_EVENTS: Record<string, EventType[]> = {
  null: ['arrive'], arrive: ['break', 'leave'], break: ['resume'],
  resume: ['break', 'leave'], leave: ['arrive']
}
const EVENT_COLORS: Record<EventType, string> = {
  arrive: 'bg-green-500 hover:bg-green-400', break: 'bg-amber-500 hover:bg-amber-400',
  resume: 'bg-blue-500 hover:bg-blue-400',   leave: 'bg-red-500 hover:bg-red-400'
}
const EVENT_ICONS: Record<EventType, React.ReactNode> = {
  arrive: <LogIn size={28} />, break: <Coffee size={28} />,
  resume: <Play size={28} />,  leave: <LogOut size={28} />
}

// ── Composant ─────────────────────────────────────────────────────
export default function Badge() {
  const [screen, setScreen]               = useState<Screen>('pin')
  const [pin, setPin]                     = useState('')
  const [employee, setEmployee]           = useState<Employee | null>(null)
  const [lastEvent, setLastEvent]         = useState<string | null>(null)
  const [plannedShift, setPlannedShift]   = useState<Shift | null>(null)
  const [confirmedEvent, setConfirmedEvent] = useState<EventType | null>(null)
  const [ruleApplied, setRuleApplied]     = useState<string | null>(null)
  const [adjustedTime, setAdjustedTime]   = useState<string | null>(null)
  const [lateMinutes, setLateMinutes]     = useState<number | null>(null)
  const [error, setError]                 = useState('')
  const [errorMessage, setErrorMessage]   = useState('')
  const [loading, setLoading]             = useState(false)
  const [establishmentId, setEstablishmentId] = useState('')
  const [time, setTime]                   = useState(new Date())
  const [isOnline, setIsOnline]           = useState(navigator.onLine)
  const [pendingQueue, setPendingQueue]   = useState<QueuedEvent[]>(loadQueue)
  const [syncing, setSyncing]             = useState(false)
  const [offlineConfirmed, setOfflineConfirmed] = useState(false)

  // ── Flush queue offline ─────────────────────────────────────────
  const flushQueue = useCallback(async () => {
    const queue = loadQueue()
    if (queue.length === 0 || syncing) return
    setSyncing(true)
    const remaining: QueuedEvent[] = []
    for (const entry of queue) {
      try {
        await axios.post(`${API}/clock`, {
          employeeId:     entry.employeeId,
          establishmentId: entry.establishmentId,
          eventType:      entry.eventType,
          deviceType:     'tablet',
          recordedAt:     entry.queuedAt   // horodatage original
        })
      } catch {
        remaining.push(entry)   // si encore en échec, on remet dans la queue
      }
    }
    saveQueue(remaining)
    setPendingQueue(remaining)
    setSyncing(false)
  }, [syncing])

  useEffect(() => {
    const id = localStorage.getItem('kioskEstablishmentId') || ''
    setEstablishmentId(id)
    const t = setInterval(() => setTime(new Date()), 1000)

    const handleOnline = () => {
      setIsOnline(true)
      if (screen === 'offline') setScreen('pin')
      // Sync différée : lancer le flush dès la reconnexion (règle 25.2)
      setTimeout(() => flushQueue(), 1500)
    }
    const handleOffline = () => {
      setIsOnline(false)
      setScreen('offline')
    }
    window.addEventListener('online',  handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      clearInterval(t)
      window.removeEventListener('online',  handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [screen, flushQueue])

  function pressDigit(d: string) {
    if (pin.length >= 6) return
    setPin(p => p + d)
    setError('')
  }

  function pressDelete() {
    setPin(p => p.slice(0, -1))
    setError('')
  }

  async function submitPin() {
    if (pin.length < 4) { setError('PIN trop court (min. 4 chiffres)'); return }
    setLoading(true)
    try {
      const res = await axios.post(`${API}/clock/verify-pin`, { pin, establishmentId })
      // Cache PIN→employé pour mode offline (règle 25.2)
      const cache = loadPinCache()
      cache[pin] = { employee: res.data.employee, lastEvent: res.data.lastEvent, cachedAt: Date.now() }
      savePinCache(cache)

      setEmployee(res.data.employee)
      setLastEvent(res.data.lastEvent)
      setPlannedShift(res.data.shift || null)
      setPin('')
      setScreen('action')
    } catch (e: any) {
      if (!navigator.onLine) {
        // Règle 25.2 : mode offline — utiliser le cache PIN si disponible
        const cache = loadPinCache()
        const cached = cache[pin]
        if (cached && Date.now() - cached.cachedAt < PIN_CACHE_TTL) {
          setEmployee(cached.employee)
          setLastEvent(cached.lastEvent)
          setPlannedShift(null)
          setPin('')
          setScreen('action')
        } else {
          setScreen('offline')
          setPin('')
        }
      } else {
        setErrorMessage(e.response?.data?.error || 'PIN incorrect ou accès refusé')
        setScreen('error')
        setTimeout(() => setScreen('pin'), 4000)
        setPin('')
      }
    } finally {
      setLoading(false)
    }
  }

  async function recordEvent(eventType: EventType) {
    if (!employee) return
    setLoading(true)

    if (!navigator.onLine) {
      // Règle 25.2 : mode offline — enqueue l'événement localement
      const entry: QueuedEvent = {
        id:              genId(),
        employeeId:      employee.id,
        employeeName:    `${employee.firstName} ${employee.lastName}`,
        establishmentId,
        eventType,
        queuedAt:        new Date().toISOString()
      }
      const q = [...loadQueue(), entry]
      saveQueue(q)
      setPendingQueue(q)

      // Mettre à jour le cache last event
      const cache = loadPinCache()
      for (const p of Object.keys(cache)) {
        if (cache[p].employee.id === employee.id) {
          cache[p].lastEvent = eventType
          cache[p].cachedAt  = Date.now()
        }
      }
      savePinCache(cache)

      setConfirmedEvent(eventType)
      setRuleApplied(null)
      setAdjustedTime(null)
      setLateMinutes(null)
      setOfflineConfirmed(true)
      setScreen('confirm')
      setTimeout(() => {
        setEmployee(null); setLastEvent(null); setPlannedShift(null)
        setConfirmedEvent(null); setOfflineConfirmed(false)
        setScreen('pin')
      }, 4000)
      setLoading(false)
      return
    }

    try {
      const res = await axios.post(`${API}/clock`, {
        employeeId: employee.id, establishmentId, eventType, deviceType: 'tablet'
      })
      setConfirmedEvent(eventType)
      setRuleApplied(res.data.ruleApplied || null)
      setAdjustedTime(res.data.adjustedTime || null)
      setLateMinutes(res.data.lateMinutes ?? null)
      setOfflineConfirmed(false)
      setScreen('confirm')
      // Update cache last event
      const cache = loadPinCache()
      for (const p of Object.keys(cache)) {
        if (cache[p].employee.id === employee.id) { cache[p].lastEvent = eventType }
      }
      savePinCache(cache)
      setTimeout(() => {
        setEmployee(null); setLastEvent(null); setPlannedShift(null)
        setConfirmedEvent(null); setRuleApplied(null); setAdjustedTime(null)
        setLateMinutes(null); setScreen('pin')
      }, 4000)
    } catch (e: any) {
      if (!navigator.onLine) {
        // Retry as offline
        setLoading(false)
        await recordEvent(eventType)
        return
      }
      setErrorMessage(e.response?.data?.error || 'Erreur lors du pointage — veuillez réessayer')
      setScreen('error')
      setTimeout(() => { setEmployee(null); setLastEvent(null); setPlannedShift(null); setScreen('pin') }, 5000)
    } finally {
      setLoading(false)
    }
  }

  function cancel() {
    setEmployee(null); setPin(''); setLastEvent(null); setPlannedShift(null)
    setScreen('pin'); setError('')
  }

  const availableEvents: EventType[] = NEXT_EVENTS[String(lastEvent)] || ['arrive']
  const timeStr = time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  const dateStr = time.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col items-center justify-center p-6">
      {/* Horloge */}
      <div className="mb-8 text-center">
        <div className="text-5xl font-mono font-bold text-white tracking-wider">{timeStr}</div>
        <div className="text-gray-400 mt-2 capitalize">{dateStr}</div>
      </div>

      {/* Card */}
      <div className="w-full max-w-sm bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-gray-800 px-6 py-4 flex items-center gap-3">
          <Clock size={20} className="text-blue-400" />
          <span className="font-semibold text-gray-100">Badgeuse</span>
          {pendingQueue.length > 0 && (
            <span className="ml-auto flex items-center gap-1 text-xs text-amber-300 bg-amber-900/40 px-2 py-0.5 rounded-full">
              <CloudUpload size={12} />
              {pendingQueue.length} en attente
            </span>
          )}
        </div>

        {/* PIN */}
        {screen === 'pin' && (
          <div className="p-6">
            <p className="text-center text-gray-400 text-sm mb-4">Saisissez votre PIN</p>
            <div className="flex justify-center gap-3 mb-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={`w-4 h-4 rounded-full transition-colors ${i < pin.length ? 'bg-blue-400' : 'bg-gray-700'}`} />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {['1','2','3','4','5','6','7','8','9'].map(d => (
                <button key={d} onClick={() => pressDigit(d)}
                  className="h-14 bg-gray-800 hover:bg-gray-700 rounded-xl text-xl font-semibold transition-colors active:scale-95">
                  {d}
                </button>
              ))}
              <button onClick={pressDelete}
                className="h-14 bg-gray-800 hover:bg-gray-700 rounded-xl flex items-center justify-center transition-colors active:scale-95">
                <Delete size={20} className="text-gray-400" />
              </button>
              <button onClick={() => pressDigit('0')}
                className="h-14 bg-gray-800 hover:bg-gray-700 rounded-xl text-xl font-semibold transition-colors active:scale-95">
                0
              </button>
              <button onClick={submitPin} disabled={loading || pin.length < 4}
                className="h-14 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 rounded-xl font-semibold transition-colors active:scale-95">
                OK
              </button>
            </div>
            {error && <p className="text-red-400 text-sm text-center mt-4">{error}</p>}
            {/* Sync manuelle */}
            {pendingQueue.length > 0 && isOnline && (
              <button onClick={flushQueue} disabled={syncing}
                className="w-full mt-4 py-2 text-xs text-amber-300 hover:text-amber-200 flex items-center justify-center gap-1 transition-colors">
                <CloudUpload size={13} />
                {syncing ? 'Synchronisation…' : `Synchroniser ${pendingQueue.length} pointage(s) hors-ligne`}
              </button>
            )}
          </div>
        )}

        {/* Action */}
        {screen === 'action' && employee && (
          <div className="p-6">
            <div className="text-center mb-5">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-2xl font-bold mx-auto mb-3">
                {employee.firstName[0]}{employee.lastName[0]}
              </div>
              <p className="text-lg font-semibold">{employee.firstName} {employee.lastName}</p>
              {lastEvent && (
                <p className="text-gray-400 text-sm mt-0.5">
                  Dernier pointage : {EVENT_LABELS[lastEvent as EventType] || lastEvent}
                </p>
              )}
            </div>

            {plannedShift && (
              <div className="mb-4 px-3 py-2.5 bg-gray-800 rounded-xl">
                <p className="text-xs text-gray-400 mb-1">Shift prévu aujourd'hui</p>
                <div className="flex items-center gap-2">
                  {plannedShift.position && (
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: plannedShift.position.color }} />
                  )}
                  <p className="text-sm font-semibold text-white">
                    {plannedShift.startTime} – {plannedShift.endTime}
                    {plannedShift.breakMinutes > 0 && <span className="text-gray-400 font-normal"> · {plannedShift.breakMinutes}min pause</span>}
                  </p>
                  {plannedShift.position && <span className="text-xs text-gray-400">{plannedShift.position.name}</span>}
                </div>
              </div>
            )}

            {!plannedShift && (
              <div className="mb-4 px-3 py-2.5 bg-gray-800 rounded-xl flex items-center gap-2">
                <AlertCircle size={14} className="text-amber-400 flex-shrink-0" />
                <p className="text-xs text-amber-300">
                  {isOnline ? 'Aucun shift planifié aujourd\'hui' : 'Mode hors-ligne — shift inconnu'}
                </p>
              </div>
            )}

            {!isOnline && (
              <div className="mb-3 px-3 py-2 bg-amber-900/30 rounded-xl flex items-center gap-2">
                <WifiOff size={13} className="text-amber-400 flex-shrink-0" />
                <p className="text-xs text-amber-300">Hors-ligne — pointage mis en file d'attente</p>
              </div>
            )}

            <div className="space-y-3">
              {availableEvents.map(ev => (
                <button key={ev} onClick={() => recordEvent(ev)} disabled={loading}
                  className={`w-full h-14 ${EVENT_COLORS[ev]} rounded-xl flex items-center justify-center gap-3 font-semibold text-lg transition-colors active:scale-95 disabled:opacity-50`}>
                  {EVENT_ICONS[ev]}
                  {EVENT_LABELS[ev]}
                </button>
              ))}
            </div>
            <button onClick={cancel} className="w-full mt-4 py-2 text-gray-500 hover:text-gray-300 text-sm transition-colors">
              Annuler
            </button>
          </div>
        )}

        {/* Confirmation */}
        {screen === 'confirm' && confirmedEvent && employee && (
          <div className="p-8 text-center">
            <CheckCircle size={56} className={`mx-auto mb-4 ${offlineConfirmed ? 'text-amber-400' : 'text-green-400'}`} />
            <p className="text-xl font-bold">{EVENT_LABELS[confirmedEvent]} enregistrée</p>
            <p className="text-gray-400 mt-1">{employee.firstName} {employee.lastName}</p>
            <p className="text-gray-400 text-sm mt-1">{timeStr}</p>
            {offlineConfirmed && (
              <div className="mt-4 px-4 py-2 rounded-xl text-sm bg-amber-900/40 text-amber-300 flex items-center justify-center gap-2">
                <CloudUpload size={14} />
                Hors-ligne — sera synchronisé à la reconnexion
              </div>
            )}
            {!offlineConfirmed && ruleApplied && (
              <div className={`mt-4 px-4 py-2 rounded-xl text-sm ${lateMinutes && lateMinutes > 5 ? 'bg-amber-900/50 text-amber-300' : 'bg-green-900/50 text-green-300'}`}>
                {ruleApplied}
                {adjustedTime && adjustedTime !== timeStr.slice(0, 5) && (
                  <span className="block text-xs mt-0.5">Heure comptabilisée : {adjustedTime}</span>
                )}
              </div>
            )}
            <p className="text-gray-600 text-sm mt-5">Retour automatique dans 4 secondes…</p>
          </div>
        )}

        {/* Offline */}
        {screen === 'offline' && (
          <div className="p-8 text-center">
            <WifiOff size={56} className="text-amber-400 mx-auto mb-4" />
            <p className="text-xl font-bold text-white">Mode hors-ligne</p>
            <p className="text-gray-400 mt-2 text-sm leading-relaxed">
              La connexion réseau est interrompue.<br />
              Ressaisissez votre PIN — si connu, le pointage sera mis en file d'attente.
            </p>
            <div className="mt-5 flex items-center justify-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
              <span className="text-amber-300 text-sm">En attente de reconnexion…</span>
            </div>
            {pendingQueue.length > 0 && (
              <p className="text-amber-400/70 text-xs mt-3">{pendingQueue.length} pointage(s) en attente de synchronisation</p>
            )}
          </div>
        )}

        {/* Error */}
        {screen === 'error' && (
          <div className="p-8 text-center">
            <XCircle size={56} className="text-red-400 mx-auto mb-4" />
            <p className="text-xl font-bold text-white">Erreur</p>
            <p className="text-gray-400 mt-2 text-sm">{errorMessage}</p>
            <p className="text-gray-600 text-xs mt-5">Retour automatique dans quelques secondes…</p>
          </div>
        )}
      </div>

      {!establishmentId && screen === 'pin' && (
        <p className="mt-6 text-xs text-gray-600 text-center max-w-xs">
          Configuration requise : connectez-vous en tant que manager et activez la badgeuse depuis le tableau de bord.
        </p>
      )}

      {!isOnline && screen !== 'offline' && (
        <div className="mt-4 flex items-center gap-2 bg-amber-900/50 text-amber-300 text-xs px-4 py-2 rounded-lg">
          <WifiOff size={14} /> Hors-ligne
          {pendingQueue.length > 0 && <span>· {pendingQueue.length} en attente</span>}
        </div>
      )}
    </div>
  )
}
